import React, {useMemo} from 'react';
import {Pressable, StyleSheet, View} from 'react-native';
import {useQuery} from '@tanstack/react-query';
import {useTranslation} from 'react-i18next';
import type {NativeStackScreenProps} from '@react-navigation/native-stack';

import type {OverviewStackParamList} from '@/app/routes';
import {fetchCompanyAttendance} from '@/entities/attendance/api';
import {currentMonth, isoDate, periodKey} from '@/entities/attendance/period';
import {fetchDashboard} from '@/entities/dashboard/api';
import type {DashboardAlertDevice} from '@/entities/dashboard/types';
import {fetchDevices} from '@/entities/device/api';
import {deviceTypeIcon, isCameraLike} from '@/entities/device/types';
import {fetchLogs} from '@/entities/log/api';
import {dayWindow, fetchLogsInRange, rangeWindow} from '@/entities/log/day';
import type {AccessLogRecord} from '@/entities/log/types';
import {useAuthStore} from '@/entities/session/auth-store';
import {PersonAvatar} from '@/entities/personnel/ui/PersonAvatar';
import {attendanceRate} from '@/features/view-attendance/lib/aggregate';
import {
  absentToday,
  lastDays,
  lateToday,
  passesByDay,
  passesByHour,
} from '@/features/view-overview/lib/pulse';
import {buildPresence, presenceCounts} from '@/features/view-presence/lib/presence';
import {ContextPlaceholder} from '@/widgets/context-placeholder/ContextPlaceholder';
import {useBreakpoint} from '@/shared/lib/breakpoints';
import {
  formatClock,
  formatCount,
  formatDayOfMonth,
  formatEventTime,
  formatHoursTotal,
  formatTimestamp,
} from '@/shared/lib/format';
import {BarChart} from '@/shared/ui/bar-chart';
import {IconBubble} from '@/shared/ui/card';
import {EmptyState} from '@/shared/ui/empty-state';
import {Icon, type IconName} from '@/shared/ui/icons';
import {Page} from '@/shared/ui/page';
import {Section, SectionHeader} from '@/shared/ui/section';
import {Skeleton} from '@/shared/ui/skeleton';
import {StatBar, type StatItem} from '@/shared/ui/stats';
import {StatusDot, StatusPill} from '@/shared/ui/status';
import {AppText} from '@/shared/ui/text';
import {radii, spacing, useTheme} from '@/shared/ui/theme';

/**
 * «Главная» — пульт дня: что происходит сейчас, что было на неделе, куда смотреть.
 *
 * ## Из чего сложена
 *
 * Ни одного нового маршрута: всё, что здесь нарисовано, гейтвей уже отдаёт другим
 * экранам, а главная только ставит рядом.
 *
 *   1. Четыре карточки-числа: проходы за сегодня, кто на объекте сейчас, кто опоздал,
 *      живы ли терминалы. Каждая — с второй строкой «из чего сложилось».
 *   2. Два графика: проходы за семь дней (журнал за неделю по дням) и сегодняшний день
 *      по часам — когда на объекте пик.
 *   3. Последние проходы — и рядом три коротких списка, которые иначе прячутся по
 *      разделам: кто внутри, кто опоздал, какие терминалы на связи.
 *   4. Полоса «этот месяц» — явка и дисциплина с первого числа, дверь в отчёты.
 *
 * Запросы независимы: медленный отчёт явки не держит цифры проходов. Ключи кэша те
 * же, что у разделов («кто на объекте», устройства, отчёт за месяц), — открыв раздел,
 * человек застаёт уже привезённое.
 *
 * ## Плитки разделов — только на телефоне
 *
 * На мониторе слева стоит боковая панель, и повторять её плитками — тратить половину
 * экрана на навигацию в двадцати сантиметрах левее. На телефоне панели нет, и плитка с
 * вопросом, на который раздел отвечает, — единственный ответ на «а где посмотреть, кто
 * прошёл» для того, кто открыл кабинет впервые.
 */
type Props = NativeStackScreenProps<OverviewStackParamList, 'Overview'>;

/** Сколько событий показать, прежде чем главная станет лентой. */
const RECENT_LIMIT = 8;
/** Сколько строк в коротких списках справа. */
const SHORT_LIST = 5;
/** Дней в тренде. */
const TREND_DAYS = 7;

const styles = StyleSheet.create({
  list: {
    borderWidth: StyleSheet.hairlineWidth,
    borderRadius: radii.xl,
    overflow: 'hidden',
  },
  row: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing(3),
    paddingHorizontal: spacing(4),
    paddingVertical: spacing(3),
  },
  rowTight: {paddingVertical: spacing(2) + 2},
  rowBody: {flex: 1, minWidth: 0, gap: 2},
  divider: {height: StyleSheet.hairlineWidth},
  loading: {gap: spacing(3)},

  /* Две колонки на широком окне: графики 2:1, ленты 3:2. */
  columns: {flexDirection: 'row', alignItems: 'flex-start', gap: spacing(5)},
  colWide: {flex: 2, minWidth: 0},
  colNarrow: {flex: 1, minWidth: 0},
  col3: {flex: 3, minWidth: 0},
  col2: {flex: 2, minWidth: 0, gap: spacing(6)},
  card: {
    borderWidth: StyleSheet.hairlineWidth,
    borderRadius: radii.xl,
    padding: spacing(4),
    gap: spacing(3),
  },
  cardHead: {gap: 2, marginBottom: spacing(1)},
  empty: {paddingVertical: spacing(3)},
  more: {paddingHorizontal: spacing(4), paddingVertical: spacing(2)},

  tiles: {flexDirection: 'row', flexWrap: 'wrap', gap: spacing(2)},
  tile: {
    flexGrow: 1,
    flexBasis: 150,
    gap: spacing(2),
    padding: spacing(4),
    borderWidth: StyleSheet.hairlineWidth,
    borderRadius: radii.xl,
  },
  tileText: {gap: 2},
});

/** Одно событие: время, кто, где, чем кончилось. */
function EventRow({companyId, log}: {companyId: string; log: AccessLogRecord}) {
  const {t} = useTranslation();
  const name = log.person_name || t('home.feed.unknownPerson');

  return (
    <View style={styles.row}>
      <PersonAvatar
        companyId={companyId}
        recordId={log.granted ? log.record_id : null}
        name={name}
        size={36}
      />
      <View style={styles.rowBody}>
        <AppText variant="subtitle" numberOfLines={1}>
          {name}
        </AppText>
        <AppText variant="caption" numberOfLines={1}>
          {formatEventTime(log.created_at_millis)} · {log.device_name}
        </AppText>
      </View>
      <StatusPill
        tone={log.granted ? 'granted' : 'denied'}
        size="sm"
        dot={false}
        label={t(log.granted ? 'home.feed.granted' : 'home.feed.denied')}
      />
    </View>
  );
}

/** Терминал, отставший от синхронизации. */
function StaleRow({device}: {device: DashboardAlertDevice}) {
  const {t} = useTranslation();
  const p = useTheme();
  return (
    <View style={styles.row}>
      <Icon name="warning" size={18} color={p.warningInk} />
      <View style={styles.rowBody}>
        <AppText variant="subtitle" numberOfLines={1}>
          {device.device_name}
        </AppText>
        <AppText variant="caption" numberOfLines={1}>
          {device.location_label || '—'}
        </AppText>
      </View>
      <AppText variant="caption" color={p.warningInk}>
        {t('dashboard.minutesSinceSync', {value: device.minutes_since_sync})}
      </AppText>
    </View>
  );
}

/** Плитка раздела: значок, имя и ВОПРОС, на который раздел отвечает. */
function SectionTile({
  icon,
  title,
  hint,
  onPress,
  testID,
}: {
  icon: IconName;
  title: string;
  hint: string;
  onPress: () => void;
  testID?: string;
}) {
  const p = useTheme();
  return (
    <Pressable
      testID={testID}
      accessibilityRole="button"
      accessibilityLabel={`${title}. ${hint}`}
      onPress={onPress}
      style={({pressed}) => [
        styles.tile,
        {backgroundColor: pressed ? p.accent : p.card, borderColor: p.border},
      ]}>
      <Icon name={icon} size={20} color={p.primary} />
      <View style={styles.tileText}>
        <AppText variant="subtitle" numberOfLines={1}>
          {title}
        </AppText>
        <AppText variant="caption" numberOfLines={2}>
          {hint}
        </AppText>
      </View>
    </Pressable>
  );
}

/** Обрамление списка строк: одна рамка, волосяные линии внутри. */
function RowList({children}: {children: React.ReactNode}) {
  const p = useTheme();
  const rows = React.Children.toArray(children).filter(Boolean);
  return (
    <View style={[styles.list, {backgroundColor: p.card, borderColor: p.border}]}>
      {rows.map((row, i) => (
        <React.Fragment key={i}>
          {i > 0 ? <View style={[styles.divider, {backgroundColor: p.border}]} /> : null}
          {row}
        </React.Fragment>
      ))}
    </View>
  );
}

/** Карточка-виджет: имя, подпись, ссылка справа и тело. */
function Widget({
  title,
  hint,
  actionLabel,
  onAction,
  children,
  testID,
}: {
  title: string;
  hint?: string;
  actionLabel?: string;
  onAction?: () => void;
  children: React.ReactNode;
  testID?: string;
}) {
  const p = useTheme();
  return (
    <View
      testID={testID}
      style={[styles.card, {backgroundColor: p.card, borderColor: p.border}]}>
      <SectionHeader
        title={title}
        hint={hint}
        actionLabel={actionLabel}
        onAction={onAction}
        style={styles.cardHead}
      />
      {children}
    </View>
  );
}

/** «ещё N» под коротким списком — дверь в раздел. */
function MoreLink({count, onPress}: {count: number; onPress: () => void}) {
  const {t} = useTranslation();
  const p = useTheme();
  if (count <= 0) {
    return null;
  }
  return (
    <Pressable accessibilityRole="link" onPress={onPress} style={styles.more}>
      <AppText variant="label" color={p.primary}>
        {t('dashboard.more', {count})}
      </AppText>
    </Pressable>
  );
}

export function OverviewScreen({navigation}: Props) {
  const {t} = useTranslation();
  const p = useTheme();
  const {isDesktop} = useBreakpoint();
  const companyId = useAuthStore(s => s.activeContext?.companyId);
  const kind = useAuthStore(s => s.activeContext?.kind);

  // Сутки берутся один раз за монтирование — как на «Кто на объекте».
  const today = useMemo(() => isoDate(new Date()), []);
  const week = useMemo(() => lastDays(today, TREND_DAYS), [today]);
  const month = useMemo(() => currentMonth(), []);

  /**
   * Переход в СОСЕДНЮЮ вкладку — через родителя: вкладки собираются в рантайме по
   * персоне, статически известного списка параметров у навигатора вкладок нет.
   */
  const openTab = (tab: string, params?: object) => {
    const parent = navigation.getParent() as unknown as
      | {navigate: (route: string, params?: object) => void}
      | undefined;
    parent?.navigate(tab, params);
  };

  const board = useQuery({
    queryKey: ['dashboard', companyId],
    queryFn: () => fetchDashboard(companyId as string),
    enabled: !!companyId,
  });

  const recent = useQuery({
    queryKey: ['logs', companyId, 'recent'],
    queryFn: () => fetchLogs(companyId as string, {limit: RECENT_LIMIT, offset: 0}),
    enabled: !!companyId,
  });

  // Тот же ключ, что у «Кто на объекте»: открыв раздел, человек застаёт привезённое.
  const presence = useQuery({
    queryKey: ['presence', companyId, today],
    queryFn: () =>
      fetchLogsInRange(companyId as string, {result: 'granted'}, dayWindow(today)),
    enabled: !!companyId,
  });

  // Неделя проходов — один сбор, из него и тренд по дням, и сегодняшний день по часам.
  const trend = useQuery({
    queryKey: ['overview-trend', companyId, today],
    queryFn: () =>
      fetchLogsInRange(companyId as string, {}, rangeWindow(week[0], today)),
    enabled: !!companyId,
  });

  // Сегодняшняя явка СО СТРОКАМИ ДНЕЙ: один день на человека — это дёшево, а
  // «опоздал на 14 минут» знает только строка дня.
  const todayReport = useQuery({
    queryKey: ['overview-attendance', companyId, today],
    queryFn: () =>
      fetchCompanyAttendance(
        companyId as string,
        {kind: 'range', from: today, to: today},
        {includeDays: true},
      ),
    enabled: !!companyId,
  });

  // Месяц — тем же ключом, что у «Посещаемости» без отбора по отделам.
  const monthReport = useQuery({
    queryKey: ['attendance-company', companyId, periodKey(month), ''],
    queryFn: () => fetchCompanyAttendance(companyId as string, month, {includeDays: false}),
    enabled: !!companyId,
  });

  const devices = useQuery({
    queryKey: ['devices', companyId, false],
    queryFn: () => fetchDevices(companyId as string, {perPage: 50, includeDesktops: false}),
    enabled: !!companyId,
  });

  const presenceRows = useMemo(() => buildPresence(presence.data?.events ?? []), [presence.data]);
  const counts = presenceCounts(presenceRows);
  const inside = presenceRows.filter(r => r.status === 'inside');

  const byDay = useMemo(() => passesByDay(trend.data?.events ?? [], week), [trend.data, week]);
  const byHour = useMemo(() => passesByHour(trend.data?.events ?? [], today), [trend.data, today]);

  const late = useMemo(() => lateToday(todayReport.data), [todayReport.data]);
  const absent = useMemo(() => absentToday(todayReport.data), [todayReport.data]);

  if (!companyId) {
    return <ContextPlaceholder titleKey="nav.overview" />;
  }

  const d = board.data;
  const stale = d?.stale_devices ?? [];
  const todaySummary = todayReport.data?.summary;
  // Cameras and recorders are not "devices" in this widget's sense: their `online` comes
  // from the terminal sync round they never take part in, so every one of them read as
  // offline here. They live — with their real, socket-based state — on the Cameras tab,
  // the same split the device list makes.
  const deviceRows = (devices.data?.data ?? []).filter(x => !isCameraLike(x.device_type));
  const offline = deviceRows.filter(x => !x.online).length;

  const stats: StatItem[] = [
    {
      label: t('dashboard.passesToday'),
      value: d?.total_passes_today ?? '—',
      hint: d
        ? t('dashboard.grantedDenied', {granted: d.granted_today, denied: d.denied_today})
        : null,
      testID: 'stat-passes',
    },
    {
      label: t('dashboard.insideNow'),
      value: presence.isPending ? '—' : counts.inside,
      hint:
        counts.seen > 0
          ? t('dashboard.seenOnly', {count: counts.seen})
          : d
          ? t('dashboard.ofPersonnel', {total: d.total_personnel})
          : null,
      testID: 'stat-inside',
    },
    {
      label: t('dashboard.lateToday'),
      value: todayReport.isPending ? '—' : late.length,
      tone: late.length > 0 ? p.warningInk : undefined,
      hint: todaySummary
        ? todaySummary.scheduled_days > 0
          ? t('dashboard.presentOf', {
              present: todaySummary.present_days,
              scheduled: todaySummary.scheduled_days,
            })
          : t('dashboard.noSchedule')
        : null,
      testID: 'stat-late',
    },
    {
      label: t('dashboard.devicesOnline'),
      value: d ? `${d.online_devices}/${d.online_devices + d.offline_devices}` : '—',
      tone: d && d.offline_devices > 0 ? p.warningInk : undefined,
      hint: d
        ? stale.length > 0
          ? t('dashboard.staleCount', {count: stale.length})
          : d.offline_devices > 0
          ? t('dashboard.offlineCount', {count: d.offline_devices})
          : t('dashboard.allOnline')
        : null,
      testID: 'stat-devices',
    },
  ];

  const events = recent.data?.items ?? [];
  const monthSummary = monthReport.data?.summary;
  const rate = monthSummary ? attendanceRate(monthSummary) : null;
  const hour = t('attendance.units.hour');
  const minute = t('attendance.units.minute');

  const monthStats: StatItem[] = [
    {
      label: t('dashboard.attendanceRate'),
      value: rate == null ? '—' : `${Math.round(rate * 100)}%`,
      hint: monthSummary
        ? `${formatCount(monthSummary.present_days)} / ${formatCount(monthSummary.scheduled_days)}`
        : null,
    },
    {
      label: t('dashboard.lateDays'),
      value: monthSummary ? monthSummary.late_days : '—',
      tone: monthSummary && monthSummary.late_days > 0 ? p.warningInk : undefined,
    },
    {
      label: t('dashboard.absentDays'),
      value: monthSummary ? monthSummary.absent_days : '—',
      tone: monthSummary && monthSummary.absent_days > 0 ? p.destructiveInk : undefined,
    },
    {
      label: t('dashboard.worked'),
      value: monthSummary ? formatHoursTotal(monthSummary.total_worked_minutes, hour, minute) : '—',
    },
  ];

  /** Карта разделов той персоны, в которой стоит читающий. */
  const tiles = [
    {tab: 'Journal', icon: 'logs' as IconName, title: t('nav.journal'), hint: t('home.map.journal')},
    kind === 'home'
      ? {tab: 'People', icon: 'people' as IconName, title: t('nav.people'), hint: t('home.map.people')}
      : {tab: 'Personnel', icon: 'personnel' as IconName, title: t('nav.staff'), hint: t('home.map.staff')},
    {tab: 'Reports', icon: 'attendance' as IconName, title: t('nav.attendance'), hint: t('home.map.attendance')},
    {tab: 'Devices', icon: 'devices' as IconName, title: t('nav.devices'), hint: t('home.map.devices')},
    {tab: 'Settings', icon: 'settings' as IconName, title: t('nav.settings'), hint: t('home.map.settings')},
  ];

  const passSeries = [
    {key: 'granted', label: t('home.feed.granted'), color: p.signal},
    {key: 'denied', label: t('home.feed.denied'), color: p.destructive},
  ];

  const weekChart = (
    <Widget title={t('dashboard.passesWeek')} hint={t('dashboard.passesWeekHint')} testID="overview-week">
      {trend.isPending ? (
        <Skeleton height={180} radius={radii.md} />
      ) : (
        <BarChart
          series={passSeries}
          points={byDay.map(day => ({
            label: formatDayOfMonth(day.date),
            title: day.date.slice(8, 10) + '.' + day.date.slice(5, 7),
            values: {granted: day.granted, denied: day.denied},
          }))}
          idleText={t('dashboard.trend')}
          height={150}
        />
      )}
    </Widget>
  );

  const hourChart = (
    <Widget title={t('dashboard.passesByHour')} hint={t('dashboard.passesByHourHint')} testID="overview-hours">
      {trend.isPending ? (
        <Skeleton height={180} radius={radii.md} />
      ) : (
        <BarChart
          series={passSeries}
          points={byHour.granted.map((g, h) => ({
            label: String(h).padStart(2, '0'),
            title: t('dashboard.hourRange', {
              from: `${String(h).padStart(2, '0')}:00`,
              to: `${String((h + 1) % 24).padStart(2, '0')}:00`,
            }),
            values: {granted: g, denied: byHour.denied[h]},
          }))}
          labelEvery={isDesktop ? 3 : 6}
          idleText={today.slice(8, 10) + '.' + today.slice(5, 7)}
          height={150}
        />
      )}
    </Widget>
  );

  const presenceWidget = (
    <Widget
      title={t('dashboard.presenceNow')}
      hint={t('presence.hint', {count: presenceRows.length})}
      actionLabel={t('dashboard.openPresence')}
      onAction={() => openTab('Journal', {screen: 'Journal', params: {view: 'presence'}})}
      testID="overview-presence">
      {presence.isPending ? (
        <Skeleton height={120} radius={radii.md} />
      ) : inside.length === 0 ? (
        <AppText variant="caption" style={styles.empty}>
          {t('dashboard.presenceEmpty')}
        </AppText>
      ) : (
        <RowList>
          {inside.slice(0, SHORT_LIST).map(row => (
            <View key={row.recordId} style={[styles.row, styles.rowTight]}>
              <PersonAvatar companyId={companyId} recordId={row.recordId} name={row.name} size={28} />
              <View style={styles.rowBody}>
                <AppText variant="subtitle" numberOfLines={1}>
                  {row.name}
                </AppText>
                <AppText variant="caption" numberOfLines={1}>
                  {t('dashboard.presenceSince', {time: formatClock(row.firstMillis)})} ·{' '}
                  {row.lastDeviceName}
                </AppText>
              </View>
              <StatusDot tone="online" size={8} />
            </View>
          ))}
          <MoreLink
            count={inside.length - SHORT_LIST}
            onPress={() => openTab('Journal', {screen: 'Journal', params: {view: 'presence'}})}
          />
        </RowList>
      )}
    </Widget>
  );

  const lateWidget = (
    <Widget
      title={t('dashboard.lateList')}
      hint={absent.length > 0 ? t('dashboard.absentCount', {count: absent.length}) : undefined}
      actionLabel={t('dashboard.openReports')}
      onAction={() => openTab('Reports')}
      testID="overview-late">
      {todayReport.isPending ? (
        <Skeleton height={100} radius={radii.md} />
      ) : late.length === 0 ? (
        <AppText variant="caption" style={styles.empty}>
          {t('dashboard.lateEmpty')}
        </AppText>
      ) : (
        <RowList>
          {late.slice(0, SHORT_LIST).map(row => (
            <View key={row.recordId} style={[styles.row, styles.rowTight]}>
              <PersonAvatar companyId={companyId} recordId={row.recordId} name={row.name} size={28} />
              <View style={styles.rowBody}>
                <AppText variant="subtitle" numberOfLines={1}>
                  {row.name}
                </AppText>
                <AppText variant="caption" numberOfLines={1}>
                  {formatClock(row.arrivalMillis)}
                </AppText>
              </View>
              <AppText variant="label" color={p.warningInk}>
                {t('dashboard.lateBy', {minutes: row.minutes})}
              </AppText>
            </View>
          ))}
          <MoreLink count={late.length - SHORT_LIST} onPress={() => openTab('Reports')} />
        </RowList>
      )}
    </Widget>
  );

  const devicesWidget = (
    <Widget
      title={t('dashboard.devices')}
      hint={offline > 0 ? t('dashboard.offlineCount', {count: offline}) : t('dashboard.allOnline')}
      actionLabel={t('home.map.openDevices')}
      onAction={() => openTab('Devices')}
      testID="overview-devices">
      {devices.isPending ? (
        <Skeleton height={100} radius={radii.md} />
      ) : deviceRows.length === 0 ? (
        <AppText variant="caption" style={styles.empty}>
          {t('dashboard.devicesEmpty')}
        </AppText>
      ) : (
        <RowList>
          {[...deviceRows]
            // Молчащие — сверху: ради них на этот список и смотрят.
            .sort((a, b) => Number(a.online) - Number(b.online))
            .slice(0, SHORT_LIST)
            .map(dev => (
              <View key={dev.device_id} style={[styles.row, styles.rowTight]}>
                <IconBubble size={28} tone={dev.online ? p.signalSoft : p.muted}>
                  <Icon
                    name={deviceTypeIcon(dev.device_type)}
                    size={14}
                    color={dev.online ? p.signal : p.mutedForeground}
                  />
                </IconBubble>
                <View style={styles.rowBody}>
                  <AppText variant="subtitle" numberOfLines={1}>
                    {dev.device_name}
                  </AppText>
                  <AppText variant="caption" numberOfLines={1}>
                    {t('dashboard.lastSeen', {time: formatTimestamp(dev.last_seen_at)})}
                  </AppText>
                </View>
                <StatusDot tone={dev.online ? 'online' : 'neutral'} size={8} />
              </View>
            ))}
          <MoreLink count={deviceRows.length - SHORT_LIST} onPress={() => openTab('Devices')} />
        </RowList>
      )}
    </Widget>
  );

  const recentWidget = (
    <Widget
      title={t('dashboard.recent')}
      hint={t('home.map.recentHint')}
      actionLabel={t('dashboard.allEvents')}
      onAction={() => openTab('Journal')}
      testID="overview-recent">
      {recent.isPending ? (
        <Skeleton height={200} radius={radii.md} />
      ) : events.length === 0 ? (
        <EmptyState icon="feed" title={t('home.feed.empty')} />
      ) : (
        <RowList>
          {events.map(log => (
            <EventRow key={log.event_id} companyId={companyId} log={log} />
          ))}
        </RowList>
      )}
    </Widget>
  );

  return (
    <Page
      scroll
      title={t('nav.overview')}
      subtitle={t('dashboard.subtitle')}
      refreshing={board.isRefetching || recent.isRefetching}
      onRefresh={() => {
        void board.refetch();
        void recent.refetch();
        void presence.refetch();
        void trend.refetch();
        void todayReport.refetch();
      }}
      testID="overview-screen">
      {board.isPending ? (
        <View style={styles.loading}>
          <Skeleton height={110} radius={radii.xl} />
          <Skeleton height={220} radius={radii.xl} />
          <Skeleton height={220} radius={radii.xl} />
        </View>
      ) : board.isError ? (
        <EmptyState
          tone="error"
          title={t('dashboard.error')}
          actionLabel={t('actions.retry')}
          onAction={() => void board.refetch()}
        />
      ) : (
        <>
          <Section>
            <StatBar testID="overview-stats" items={stats} columns={4} />
          </Section>

          {stale.length > 0 ? (
            <Section
              title={t('dashboard.needsAttention')}
              hint={t('dashboard.staleHint')}
              actionLabel={t('home.map.openDevices')}
              onAction={() => openTab('Devices')}>
              <RowList>
                {stale.slice(0, 4).map(device => (
                  <StaleRow key={device.device_id} device={device} />
                ))}
              </RowList>
            </Section>
          ) : null}

          {/* Графики: неделя шире, час-в-час у́же — форму недели читают, форму дня
              скорее замечают. */}
          <Section>
            {isDesktop ? (
              <View style={styles.columns}>
                <View style={styles.colWide}>{weekChart}</View>
                <View style={styles.colNarrow}>{hourChart}</View>
              </View>
            ) : (
              <View style={styles.loading}>
                {weekChart}
                {hourChart}
              </View>
            )}
          </Section>

          {/* Лента и три коротких списка, за которыми иначе идут по разделам. */}
          <Section>
            {isDesktop ? (
              <View style={styles.columns}>
                <View style={styles.col3}>{recentWidget}</View>
                <View style={styles.col2}>
                  {presenceWidget}
                  {lateWidget}
                  {devicesWidget}
                </View>
              </View>
            ) : (
              <View style={styles.col2}>
                {presenceWidget}
                {lateWidget}
                {recentWidget}
                {devicesWidget}
              </View>
            )}
          </Section>

          <Section
            title={t('dashboard.month')}
            hint={t('dashboard.monthHint')}
            actionLabel={t('dashboard.openReports')}
            onAction={() => openTab('Reports')}>
            {monthReport.isPending ? (
              <Skeleton height={96} radius={radii.xl} />
            ) : (
              <StatBar testID="overview-month" items={monthStats} columns={4} />
            )}
          </Section>

          {isDesktop ? null : (
            <Section title={t('home.map.title')} hint={t('home.map.hint')}>
              <View style={styles.tiles} testID="overview-map">
                {tiles.map(tile => (
                  <SectionTile
                    key={tile.tab}
                    icon={tile.icon}
                    title={tile.title}
                    hint={tile.hint}
                    onPress={() => openTab(tile.tab)}
                    testID={`overview-map-${tile.tab}`}
                  />
                ))}
              </View>
            </Section>
          )}
        </>
      )}
    </Page>
  );
}
